// The tools a voice conversation calls, answered from the panel — shared by the conversation on
// OpenAI's voice model (realtime.ts, T67) and on ElevenLabs' agent (elevenlabs.ts, 23 Sep 2026).
//
// WHY ONE FILE. Whoever hears and speaks for kadmon, the tools mean the same: `ask_vera` is the
// panel's own ask, and the three lookups (`look_up`, `look_up_search`, `look_up_waiting`) the brain
// API's reads. The vendor's file says when the recruiter spoke (`heard`), and hands each call's name
// and arguments to `run`; what goes back is `ToolOutput`, which the vendor serialises its own way.
//
// NOTHING SPOKEN APPLIES A CARD (T595, Natanel, 2 Nov 2026: "Remove spoken yes from Talk
// (Recommended)", replacing his 23 Sep 2026 "a spoken yes should work"). An instruction is done at
// once (T429), so a card that stays is one kept for a tap: a lifted opt-out above all. No tool here
// reaches its Confirm, and the voice says it is waiting for that tap.
//
// IT IMPORTS NOTHING THAT NEEDS A BROWSER, on purpose: the two voice checks on the brain API
// (services/bff/src/scripts/voice-check-lines.ts) answer the real models' tool calls with THIS
// code, so a check cannot pass a behaviour the brain would refuse. voice-mode.ts pulls in the
// orb's canvas types, which the brain API does not have, so the shapes below are voice-mode.ts's
// `VoiceCallbacks` and `VoiceSnapshot` as far as the tools use them — the two stores pass theirs
// in, and the typecheck holds them compatible.
//
// AN ANSWER THAT ARRIVES AFTER THE RECRUITER MOVED ON IS HANDED TO HER AT ONCE, AND SHE DECIDES
// (T449, 24 Oct 2026, Natanel: "measuring it by after 'x' seconds is not fluid or how humans speak
// … if I interrupt then it judges based off my interruption words what to do"). T436 held such an
// answer until she had finished and they were quiet, which a "Hello?" restarted for ever; no timer
// decides now. If they spoke again while a lookup ran, the answer goes back marked `earlier`
// (`ask`), and where the vendor throws away a result that arrives after the recruiter spoke
// (`dropsLateResults`: ElevenLabs, measured at T449, 5 replays and a socket test) the tool returns
// `later` and the answer reaches her as a line from the brain, told when nobody is speaking
// (`lateAnswerLine`, `told`). She weighs it against what was said since: gives it, gives it after
// their new question, or leaves it.

/** What an ask hears while it runs, as far as the tools pass it on (voice-mode.ts `VoiceProgress`). */
export interface ToolAskProgress {
  working(): void;
  sentence(text: string): void;
  answer?(text: string, cards: number): void;
}

/**
 * Where an `ask_vera` is, for whoever fills its wait (T120a, Talk's fillers, voice-fillers.ts): the
 * find reached for records (said once a tool round), and its answer began to be written (said once
 * a sentence). Nothing else of the ask is passed on.
 */
export interface LookupStages {
  looking(): void;
  writing(): void;
}

/** The panel's ask and lookups (voice-mode.ts `VoiceCallbacks`), as the tools call them. */
export interface ToolCallbacks {
  ask(
    text: string,
    on: ToolAskProgress,
  ): Promise<{
    answer: string;
    card: string | null;
    showing: boolean;
    /** 24 Sep 2026: whom the answer as handed over named, who have no card after all. */
    unshown?: string[];
  } | null>;
  /**
   * Her voice's own lookups (23 and 24 Sep 2026): a plain fact about one named person, who is on
   * a Search, who is waiting on the recruiter — read from the records by the brain API
   * (`POST /voice/lookup`, services/bff/src/voice-lookup.ts), which writes the chat's row whenever
   * it answers. `found` is that route's answer.
   */
  lookUp(input: LookupInput): Promise<ToolLookup>;
}

/** What a lookup asks: which kind, whose — a person's name or a Search's — which facts, the words. */
export interface LookupInput {
  kind: 'person' | 'search' | 'waiting';
  name: string;
  facts: string;
  question: string;
}

/** The lookup's answer, as `POST /voice/lookup` gives it (`VoiceLookupResponse`) less the row's ids. */
export type ToolLookup =
  | { found: 'answer'; said: Record<string, string> }
  | { found: 'several'; options: string[] }
  | { found: 'nobody' }
  | { found: 'not-a-fact' };

/** The lookups' tool names, and the kind of lookup each asks for. */
const LOOKUP_TOOLS: Readonly<Record<string, LookupInput['kind']>> = {
  look_up: 'person',
  look_up_search: 'search',
  look_up_waiting: 'waiting',
};

/** What of the conversation's snapshot the tools read and write. */
export interface ToolSnapshot {
  card: string | null;
  showing: boolean;
  /** Whether she is speaking — when a correction may be told (`tell`). Read, never written. */
  state?: string;
}

/** What a tool answers the voice model with — JSON it reads, never the recruiter. */
export type ToolOutput =
  | { answer: string; cardWaiting: boolean; earlier?: string }
  | { later: string }
  | { error: string }
  | { waitsForTap: string }
  | { found: Record<string, string> }
  | { several: string[]; say: string }
  | { askVera: string };

/**
 * What a lookup tells the voice when the name fits several people or Searches, or only by a sound:
 * the brain picks the likelier and says which (T595, Natanel, 2 Nov 2026: "Pass it to the full brain
 * (Recommended)").
 */
export const LOOKUP_SEVERAL =
  'Not sure which they meant. Call ask_vera with their question as they said it.';

/** What a lookup tells the voice when it cannot answer: the question is kadmon's. */
export const LOOKUP_ASK_VERA = {
  nobody: 'Nothing on file by that name. Ask kadmon: call ask_vera with their question.',
  'not-a-fact': 'That is not one of the facts look_up reads. Call ask_vera with their question.',
} as const;

/**
 * T436, T449: an answer that comes back after the recruiter has said more since asking. It rides
 * with the answer, so she weighs it against what they said since rather than reading it as the
 * reply to what was said last.
 */
export const EARLIER_ANSWER =
  'They have said more since asking this. Decide from what they said since whether this answer still fits: give it, in a way that shows it answers their earlier question and before you answer anything they asked after it, or leave it.';

/**
 * T449: what the tool returns where the answer reaches her as a line instead (`dropsLateResults`).
 * The answer is never in this result, so it cannot be said twice.
 */
export const ANSWER_COMES_AS_LINE =
  'They have said more since asking this. The answer reaches you as a line from the brain once it is ready; you have nothing to say about it now, so reply to what they said since.';

/**
 * T449: the answer to an earlier question, told as a line of the brain's (rule 8 of her script).
 * `said`: what the recruiter said since asking, or null when nothing was; a line of the brain's
 * alone between (an earlier answer told) leaves nothing to have moved on from, so she is told to
 * give it. The words are quoted so she weighs them herself rather than going by the transcript.
 */
export function lateAnswerLine(
  question: string,
  answer: string,
  said: readonly string[] | null,
): string {
  const rest = said
    ? (said.length > 0 ? `What they said since: ${said.map((w) => `"${w}"`).join(' ')} ` : '') +
      'Answer as a person would: give it, before you answer anything they asked after it. Adding ' +
      'another question does not drop it. But if what they said since takes the earlier question ' +
      'back or replaces it (they told you to forget it, said never mind, or asked for something ' +
      'else instead), do not give the answer: say in one short line that you will leave it, and ' +
      'why. '
    : 'They have not said anything since, so give it. ';
  return `${FROM_THE_BRAIN} The answer to their earlier question "${question}" is in: ${answer} ${rest}Call no tool.`;
}

/** T449: an earlier question that got no answer, told the same way. */
export function lateErrorLine(question: string, error: string): string {
  return (
    `${FROM_THE_BRAIN} Their earlier question "${question}" did not get an answer: ${error} ` +
    'Say so in one short sentence, unless they moved on from it. Call no tool.'
  );
}

/**
 * T595: an email address is never handed to the voice vendor (docs/decisions.md). An answer of the
 * brain's goes through here, and what a lookup found, whose message may hold one, whole or cut short.
 */
const EMAIL_ADDRESS = /[^\s@<>()",;:“”‘]+@(?:[^\s@<>()"',;:“”‘’]*[^\s@<>()"',;:“”‘’.!?])?/gu;
const withoutEmail = (text: string): string => text.replace(EMAIL_ADDRESS, 'on screen');

const FAILED = 'That did not go through; ask the recruiter to try again or type it.';

/**
 * A holdover (T595a removes it). Talk's live agent on ElevenLabs holds its tools on their side, so
 * until it is remade after T595's Release it can still call these two. They apply nothing: an
 * undefined tool would come back as a failure, and rule 6 would have it tell the recruiter to type it.
 */
export const HOLDOVER_CARD_TOOLS = ['confirm_card', 'dismiss_card'] as const;

/** What a holdover call answers: nothing spoken applies a card. */
export const CARD_WAITS_FOR_TAP = 'Not applied: this waits for their tap on screen.';

/** What the tools need of the conversation they belong to. */
export interface VoiceToolHost {
  callbacks(): ToolCallbacks | null;
  snapshot(): ToolSnapshot;
  /** The card and what is on screen; the state is the vendor's to set, never the tools'. */
  set(patch: Partial<Pick<ToolSnapshot, 'card' | 'showing'>>): void;
  /** Bumped by start and end: a result from before the bump is dropped (`run` answers null). */
  generation(): number;
  /**
   * Hand the voice model a line from the brain, now, for it to answer aloud (24 Sep 2026): a
   * correction to what she has just said (`correctionFor`). Absent where there is no model to
   * tell — the local loop reads its own correction (voice-mode.ts).
   */
  tell?(line: string): void;
  /**
   * T449: set by the vendor that takes a tool's result and then never speaks it when the recruiter
   * spoke after the call was made (ElevenLabs: `agent_tool_response` success, an `interruption`,
   * no `agent_response`; handoffs/sessions/T449.md § Evidence). An answer that arrives after they
   * spoke again is then told with `tell` instead of returned.
   */
  dropsLateResults?: boolean;
  /**
   * Whether she has replied to what the recruiter said last and finished saying it (T436). Each
   * vendor answers from its own events, since the state alone cannot: ElevenLabs reads `speaking`
   * all the time a tool is pending (its typing sound), and a reply that began and ended while a
   * lookup ran leaves no trace in a state sampled later.
   */
  herReplyDone(): boolean;
  /**
   * T449 (24 Oct 2026): a line for the harness's log — when an answer is ready, when it goes back
   * to the voice model and whether the recruiter spoke since asking, when a correction is told.
   * Only `apps/console/harness/talk-measure.ts` passes one; the panel passes none.
   */
  trace?(what: string): void;
}

/** The mark on a line the brain hands her voice: it is not the recruiter (voice script rule 8). */
export const FROM_THE_BRAIN = '[the brain, not the recruiter]';

/** "A", "A and B", "A, B and C". */
const listed = (names: readonly string[]): string =>
  names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;

/**
 * What her voice is told when the answer it has already said named somebody who has no card after
 * all — 24 Sep 2026: every reason kadmon gave them quoted nothing the brain holds, so the card was
 * dropped, and the answer written again left them out. Rule 8 of her script has her say it in a
 * sentence and call nothing.
 */
export function correctionFor(names: readonly string[]): string {
  return (
    `${FROM_THE_BRAIN} A correction to what you just said: there is no card for ${listed(names)}: ` +
    'nothing the brain holds backs up what was said about them. Tell the recruiter that in one ' +
    'short sentence, and call nothing.'
  );
}

/** The same correction as the local loop's own reader says it (voice-mode.ts): no model to tell. */
export function correctionSaid(names: readonly string[]): string {
  return `A correction: there's no card for ${listed(names)}: nothing the brain holds backs up what I said about them.`;
}

/** How long she must have been quiet before a correction is told: longer than a gap between words. */
export const QUIET_BEFORE_TELLING_MS = 1_200;

/** How long a correction waits for her to finish before it is dropped — the chat holds it anyway. */
export const TELL_WITHIN_MS = 30_000;

const TELL_POLL_MS = 150;

/**
 * T436a (Natanel, 24 Oct 2026, by timing, not by words): a transcript that arrives in two parts
 * leaves its second part just after the tool began. Speech heard this soon after `ask_vera` was
 * called is the rest of that question, not the recruiter moving on.
 */
export const REST_OF_QUESTION_MS = 1_000;
// 24 Oct 2026 (T442a): ElevenLabs' real lag is NOT measured. `voice-connect.ts --overlap --runs 5`
// ran twice, headless and headful, and its fake microphone gave the agent no speech either time
// (it heard "..." and greeted). 1000 stands until a person runs `--live` and reads `lookup begins`
// against `heard` (TODO § Waiting): keep it if the worst lag is under ~700 ms, else worst + 300.

export interface VoiceTools {
  /**
   * One call. Null when the conversation ended or restarted while it ran: answer nothing. `stages`
   * hears where an `ask_vera` is while it runs (T120a).
   */
  run(
    name: string,
    args: Record<string, unknown>,
    stages?: LookupStages,
  ): Promise<ToolOutput | null>;
  /**
   * The recruiter spoke and, where the vendor hands the words over, what they said (ElevenLabs does;
   * OpenAI's call gives only that they spoke).
   * False when the words are the rest of the latest question (heard within `REST_OF_QUESTION_MS`
   * of its ask, T442a), which the vendor must not take for the recruiter moving on.
   */
  heard(words?: string): boolean;
  /** She spoke: a correction about an answer she never said is not told (`tellWhenQuiet`). */
  spoke(): void;
  /** The conversation ended: nothing waits. */
  reset(): void;
}

export function createVoiceTools(host: VoiceToolHost): VoiceTools {
  // The ask `ask_vera` is still finishing after its answer went to the model: the next ask waits for
  // it (`asked`).
  let asking: Promise<void> = Promise.resolve();
  // 24 Sep 2026: how many asks have begun and how often the recruiter has spoken — a correction
  // told after either would answer the wrong moment, so it is not told (`tellWhenQuiet`).
  let asksBegun = 0;
  let heardCount = 0;
  // T449: how many replies of hers have been heard (`spoke`), and how many lines `told` has sent:
  // a correction about an answer she never said is not told, and a told line counts as "said since"
  // for every ask still running, since the vendor drops that ask's result from then on.
  let spokeCount = 0;
  let toldCount = 0;
  type Asking = { at: number; excused: number; said: string[] };
  // T449: answers waiting to be told, one after another; `queued` counts the items not yet done.
  let chain: Promise<void> = Promise.resolve();
  let queued = 0;
  // `reset` starts the queue over: an item of the old one must not count against the new.
  let queueEpoch = 0;
  // The asks whose answer waits to be told keep hearing what the recruiter says, so the line is
  // written at the moment it is told, from all of it.
  const waiting = new Set<Asking>();
  // T449: the lookups running (`ask_vera` aside): a line is not told while one runs.
  let otherRunning = 0;
  // T436a: speech heard within `REST_OF_QUESTION_MS` of an ask's own start is the rest of that
  // question. T442a: the window is per ask, since a second ask can begin while the first runs and
  // its transcript lags the second's call, not the first's. `excused` counts what each ask was
  // spared; `latest` is the newest ask, in flight or not.
  const inFlight = new Set<Asking>();
  let latest: Asking | null = null;

  /**
   * Waits until she has been quiet (listening or muted) for `QUIET_BEFORE_TELLING_MS`, within
   * `within` ms. False if `stop()` says to give up first, or the time ran out. It restarts
   * whenever the recruiter speaks again.
   */
  const untilQuiet = async (within: number, stop: () => boolean): Promise<boolean> => {
    const until = Date.now() + within;
    let quietSince: number | null = null;
    let seen = heardCount;
    while (Date.now() < until) {
      if (stop()) return false;
      if (seen !== heardCount) {
        seen = heardCount;
        quietSince = null;
      }
      const state = host.snapshot().state;
      const now = Date.now();
      // T442a: a correction waits for her speech to finish too, not only for the state: ElevenLabs
      // reads listening while the audio of her last words still plays (`herReplyDone` estimates it).
      const resting = state === 'listening' || state === 'muted';
      const quiet = resting && host.herReplyDone();
      quietSince = quiet ? (quietSince ?? now) : null;
      if (quietSince !== null && now - quietSince >= QUIET_BEFORE_TELLING_MS) return true;
      await new Promise((resolve) => setTimeout(resolve, TELL_POLL_MS));
    }
    return false;
  };

  /**
   * A correction, told once she has finished speaking: quiet (listening or muted) for
   * `QUIET_BEFORE_TELLING_MS`, within `TELL_WITHIN_MS`. Not told if the conversation ended, the
   * recruiter spoke, or another ask began since the answer it corrects was handed over — they have
   * moved on, and the chat already shows the answer as corrected. T449: nor if no reply of hers
   * followed the answer (`spoke`).
   */
  const tellWhenQuiet = async (
    line: string,
    mine: number,
    ask: number,
    heard: number,
    spoke: number,
  ) => {
    const quiet = await untilQuiet(
      TELL_WITHIN_MS,
      () => mine !== host.generation() || ask !== asksBegun || heard !== heardCount,
    );
    // T449: only once she has replied to the answer; one she left unsaid has nothing to correct.
    if (quiet && spokeCount !== spoke) {
      host.trace?.('correction told');
      toldCount += 1;
      host.tell?.(line);
    }
  };
  /**
   * T449: waits until nobody is speaking — her state resting, `herReplyDone` (she has replied to
   * what the recruiter said last and her audio has ended, and no voice of theirs is being heard) and
   * no other tool call running. These are the vendor's events; no clock of this file's decides, and
   * a line is dropped only when the conversation ends.
   */
  const untilFree = async (stop: () => boolean): Promise<boolean> => {
    while (!stop()) {
      const state = host.snapshot().state;
      const resting = state === 'listening' || state === 'muted';
      if (resting && host.herReplyDone() && otherRunning === 0) return true;
      await new Promise((resolve) => setTimeout(resolve, TELL_POLL_MS));
    }
    return false;
  };

  /** Adds one thing to tell to the chain, after those before it. */
  const enqueue = (item: () => Promise<void>) => {
    queued += 1;
    const epoch = queueEpoch;
    chain = chain.then(item).finally(() => {
      if (epoch === queueEpoch) queued -= 1;
    });
  };

  const ask = async (
    bound: ToolCallbacks,
    question: string,
    mine: number,
    stages?: LookupStages,
  ): Promise<ToolOutput | null> => {
    const own: Asking = { at: Date.now(), excused: 0, said: [] };
    inFlight.add(own);
    latest = own;
    try {
      return await asked(bound, question, mine, own, stages);
    } finally {
      inFlight.delete(own);
    }
  };

  const asked = async (
    bound: ToolCallbacks,
    question: string,
    mine: number,
    own: Asking,
    stages?: LookupStages,
  ): Promise<ToolOutput | null> => {
    asksBegun += 1;
    const thisAsk = asksBegun;
    // T436: how often the recruiter had spoken when this question was put (it was heard just before
    // the call): an answer that arrives after more is marked, or told as a line (below). T449: a
    // line told since counts as well.
    const heardAtAsk = heardCount;
    const toldAtAsk = toldCount;
    // One ask at a time (17 Sep 2026). Her answer reaches the model while its ask is still writing
    // the matches, so the recruiter can ask again before it has finished: the next waits for it,
    // so it reads that answer in its thread and goes to the same chat.
    await asking;
    if (mine !== host.generation()) return null;
    // The answer went to the model before its cards were checked: what it said, and when.
    let handedOver = null as { heard: number } | null;
    // T436: a correction about that answer is told only once the answer itself has gone to the
    // model (T449: or been told to it), counting from how often the recruiter had spoken then.
    type Handed = { heard: number; spoke: number; ask: number };
    let release: (handed: Handed) => void = () => {};
    const released = new Promise<Handed>((resolve) => {
      release = resolve;
    });
    // T449: the correction's own telling, which the answer's place in the chain waits for.
    let corrected: Promise<void> = Promise.resolve();
    // Whichever comes first: the answer as soon as it is written, or the ask's end.
    let settle: (value: ToolOutput) => void = () => {};
    const first = new Promise<ToolOutput>((resolve) => {
      settle = resolve;
    });
    // What is on screen stays on screen while the next question runs: `showing` is set by answers
    // and cleared by nothing but the conversation's end (voice-stage.ts, 24 Sep 2026).
    const whole = bound
      .ask(question, {
        working: () => stages?.looking(),
        sentence: () => stages?.writing(),
        // T595: anything left waiting is waiting for a tap, however many things it is, as the
        // answer's own end says below.
        answer: (text, cards) => {
          handedOver ??= { heard: heardCount };
          settle({ answer: withoutEmail(text), cardWaiting: cards > 0 });
        },
      })
      .then(
        (answer) => {
          if (answer && mine === host.generation()) {
            host.set({ card: answer.card, showing: host.snapshot().showing || answer.showing });
            // 24 Sep 2026: she has already said somebody who has no card after all — she says so.
            const unshown = answer.unshown;
            if (handedOver && unshown && unshown.length > 0 && host.tell) {
              corrected = released.then((handed) =>
                tellWhenQuiet(correctionFor(unshown), mine, handed.ask, handed.heard, handed.spoke),
              );
            }
          }
          settle(
            answer
              ? { answer: withoutEmail(answer.answer), cardWaiting: answer.card !== null }
              : { error: 'kadmon could not answer this time; ask again, or type it in the brain.' },
          );
        },
        () => settle({ error: FAILED }),
      );
    asking = whole;
    const output = await first;
    if (mine !== host.generation()) return null;
    const spokenSince = heardCount - heardAtAsk - own.excused;
    const toldSince = toldCount - toldAtAsk;
    const words = 'answer' in output ? output.answer.slice(0, 50) : 'error';
    host.trace?.(`answer ready: ${words} (spoken since asking: ${spokenSince})`);
    // T449: where the vendor throws a result away that arrives after the recruiter spoke, the
    // answer goes to her as a line of the brain's once nobody is speaking, never in the result, so
    // it cannot be said twice. An answer that settles while another waits to be told joins it, so
    // the first is told first. A late error is told too.
    if (host.dropsLateResults && host.tell && (spokenSince > 0 || toldSince > 0 || queued > 0)) {
      // Written once nobody is speaking, from all they said up to then (`waiting` keeps hearing).
      waiting.add(own);
      enqueue(async () => {
        try {
          if (!(await untilFree(() => mine !== host.generation()))) return;
          const recruiterSpoke = heardCount - heardAtAsk - own.excused > 0;
          const line =
            'answer' in output
              ? lateAnswerLine(question, output.answer, recruiterSpoke ? own.said : null)
              : lateErrorLine(question, 'error' in output ? output.error : '');
          host.trace?.(`answer told: ${words}`);
          toldCount += 1;
          host.tell?.(line);
          release({ heard: heardCount, spoke: spokeCount, ask: asksBegun });
        } finally {
          waiting.delete(own);
        }
        await whole;
        await corrected;
      });
      return { later: ANSWER_COMES_AS_LINE };
    }
    const handed = { heard: handedOver?.heard ?? heardCount, spoke: spokeCount, ask: thisAsk };
    // T436, T449: they have talked on since asking: the answer goes back at once, marked, and she
    // decides what to do with it. An error is not marked.
    if ('answer' in output && spokenSince > 0) {
      release(handed);
      host.trace?.(`answer returned, earlier: ${words}`);
      return { ...output, earlier: EARLIER_ANSWER };
    }
    release(handed);
    host.trace?.(`answer returned: ${words}`);
    return output;
  };

  return {
    async run(name, args, stages) {
      const mine = host.generation();
      const bound = host.callbacks();
      const other = name !== 'ask_vera';
      if (other) otherRunning += 1;
      try {
        if (!bound) throw new Error('nothing to ask');
        if (name === 'ask_vera') {
          const question = typeof args.question === 'string' ? args.question : '';
          if (question.trim() === '') {
            return { error: 'The question was empty; ask the recruiter to say it again.' };
          }
          return await ask(bound, question, mine, stages);
        }
        if ((HOLDOVER_CARD_TOOLS as readonly string[]).includes(name)) {
          return { waitsForTap: CARD_WAITS_FOR_TAP };
        }
        const kind = LOOKUP_TOOLS[name];
        if (kind) {
          const text = (field: string) =>
            typeof args[field] === 'string' ? (args[field] as string).trim() : '';
          // Whose: a person's name, or the Search as named; waiting on the recruiter needs neither.
          const who = kind === 'person' ? text('name') : kind === 'search' ? text('search') : '';
          const facts = kind === 'person' ? text('facts') : '';
          if ((kind !== 'waiting' && !who) || (kind === 'person' && !facts)) {
            return {
              error:
                'Say whose fact and which, or which Search; or call ask_vera with their question.',
            };
          }
          // The recruiter's words are what the chat shows; the name stands in when none came.
          const question = text('question') || who || 'Who is waiting on me?';
          const found = await bound.lookUp({ kind, name: who, facts, question });
          if (mine !== host.generation()) return null;
          if (found.found === 'answer') {
            // The answer and its people's cards are in the chat now (the brain API wrote the row).
            host.set({ showing: true });
            return {
              found: Object.fromEntries(
                Object.entries(found.said).map(([key, words]) => [key, withoutEmail(words)]),
              ),
            };
          }
          if (found.found === 'several') return { several: found.options, say: LOOKUP_SEVERAL };
          return { askVera: LOOKUP_ASK_VERA[found.found] };
        }
        return { error: `Unknown tool ${name}.` };
      } catch {
        return mine === host.generation() ? { error: FAILED } : null;
      } finally {
        if (other) otherRunning -= 1;
      }
    },
    heard(words) {
      heardCount += 1;
      // The rest of a question is excused from the ask it belongs to; it is only ever known where
      // the words reach this file.
      let counted = true;
      if (words !== undefined) {
        const at = Date.now();
        for (const each of new Set([...inFlight, ...waiting])) {
          if (at - each.at <= REST_OF_QUESTION_MS) each.excused += 1;
          else each.said.push(words);
        }
        if (latest && at - latest.at <= REST_OF_QUESTION_MS) counted = false;
      }
      return counted;
    },
    spoke() {
      spokeCount += 1;
    },
    reset() {
      asking = Promise.resolve();
      inFlight.clear();
      waiting.clear();
      chain = Promise.resolve();
      queued = 0;
      queueEpoch += 1;
      latest = null;
    },
  };
}
